import { ApiService } from '../services/ApiService.js';
import { MessageStore } from './MessageStore.svelte.js';
import groupDefinitions from '../../data/groups.json';

/**
 * @typedef {import('../types/api.d.js').ApiUsersListItem} ApiUsersListItem
 * @typedef {import('../types/user.d.js').UserStoreState} UserStoreState
 */

const api = new ApiService();

class UserStoreDefinition {
    static USERNAME_REGEX = /^[A-Za-z0-9]+$/;
    static ACRONYM_REGEX = /^[A-Z]{2,3}$|^[A-Z]{2}[a-z]$|^[A-Z][a-z][A-Z]$/;

    static getNullData() {
        return {
            userId: null,
            userName: null,
            userAcronym: null,
            firstName: null,
            lastName: null,
            color: null,
            email: null,
            nickName: null,
            groups: [],
            lastError: null,
            loading: true,
            waiting: false,
            pauseWhoAmI: 0,
            lastCheck: null,
        };
    }
}

/**
 * @param {UserStoreState} state
 * @param {string} group
 */
export function hasGroup(state, group) {
    const groups = state.groups ?? [];

    if (group === 'ROOT') {
        return groups.includes('ROOT');
    }
    return groups.includes('ROOT') || groups.includes('ADMIN') || groups.includes(group);
}

class UserStoreClass {
    #state = $state(UserStoreDefinition.getNullData());

    constructor() {
        this.reset();
        setInterval(() => this.checkWhoAmI(), 4987);
        setInterval(() => this.cleanupPauseWhoAmI(), 610024); // round about 10 minutes
        void this.checkWhoAmI();
    }

    get userId() {
        return this.#state.userId;
    }
    get userName() {
        return this.#state.userName;
    }
    get userAcronym() {
        return this.#state.userAcronym;
    }
    get firstName() {
        return this.#state.firstName;
    }
    get lastName() {
        return this.#state.lastName;
    }
    get color() {
        return this.#state.color;
    }
    get email() {
        return this.#state.email;
    }
    get nickName() {
        return this.#state.nickName;
    }
    get groups() {
        return this.#state.groups;
    }
    get lastError() {
        return this.#state.lastError;
    }
    get loading() {
        return this.#state.loading;
    }
    get waiting() {
        return this.#state.waiting;
    }
    get lastCheck() {
        return this.#state.lastCheck;
    }
    get isLoggedIn() {
        return this.#state.userId !== null && this.#state.userName !== null && this.#state.userAcronym !== null;
    }
    get pauseWhoAmI() {
        return this.#state.pauseWhoAmI > 0;
    }

    reset(errorMessage = null) {
        if (errorMessage) {
            MessageStore.error(errorMessage);
        }

        this.#state = {
            ...UserStoreDefinition.getNullData(),
            lastError: errorMessage,
            loading: false,
            lastCheck: Date.now(),
        };
    }

    /**
     * @param {boolean} waiting
     */
    setWaiting(waiting) {
        waiting = !!waiting;
        this.#state.waiting = waiting;
    }

    /**
     * @param {boolean} pause
     */
    setPauseWhoAmI(pause) {
        pause = !!pause;
        let counter = this.#state.pauseWhoAmI + (pause ? 1 : -1);
        this.#state.pauseWhoAmI = Math.max(0, counter);
        console.log('pauseWhoAmI', pause, counter, this.#state.pauseWhoAmI, this.pauseWhoAmI);

        // -
        if (counter === 0) {
            void this.checkWhoAmI();
        }
    }

    async doLogin(user, password) {
        this.setWaiting(true);

        const response = await api.login(user, password);

        if (!response.ok) {
            this.reset();
            this.setWaiting(false);
            return false;
        }

        MessageStore.success('Login erfolgreich!');

        await this.checkWhoAmI(true);
        this.setWaiting(false);

        return this.isLoggedIn;
    }

    cleanupPauseWhoAmI() {
        this.setPauseWhoAmI(false);
    }

    async checkWhoAmI(ignoreWaiting = false) {
        if (this.pauseWhoAmI) {
            return;
        }
        if (!ignoreWaiting && this.#state.waiting) {
            return;
        }

        try {
            const response = await api.whoAmI();
            if (!response.ok) {
                this.reset(response.message);
                return;
            }

            const data = /** @type {ApiUsersListItem} */ (response.data);

            if (data.userId === null || data.userName === null || data.userAcronym === null) {
                this.reset();
                return;
            }

            if (data.userId <= 0 || !UserStoreDefinition.USERNAME_REGEX.test(data.userName) || !UserStoreDefinition.ACRONYM_REGEX.test(data.userAcronym)) {
                this.reset('Ungültige Benutzerdaten vom Server erhalten!');
                return;
            }

            this.#state = {
                ...UserStoreDefinition.getNullData(),
                userId: data.userId,
                userName: data.userName,
                userAcronym: data.userAcronym,
                firstName: data.firstName,
                lastName: data.lastName,
                color: data.color,
                email: data.email,
                nickName: data.nickName,
                groups: (data.groups ?? []).filter((group) => group in groupDefinitions),
                loading: false,
                waiting: false,
                pauseWhoAmI: 0,
                lastCheck: Date.now(),
            };
        } catch (error) {
            this.reset(error instanceof Error ? error.message : String(error));
        }
    }

    async doLogout() {
        this.setWaiting(true);
        await api.logout();
        this.reset();
    }

    hasGroup(group) {
        return hasGroup(this.#state, group);
    }
}

export const UserStore = new UserStoreClass();
